import { addMonths, format, getMonth, getYear, subMonths } from "date-fns";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { formatDateForAPI, getCalendarGrid, isDateDisabled, toDate } from "@shared/datetime/dateUtils";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function DatePicker({
  selectedDate,
  onSelect,
  minDate,
  maxDate,
  disabledWeekdays,
  disabledDates,
  visibleDate,
  onVisibleDateChange,
  className = "",
}) {
  const selected = toDate(selectedDate);
  const current = toDate(visibleDate) || selected || new Date();
  const grid = getCalendarGrid(getMonth(current) + 1, getYear(current));
  const selectedKey = selected ? formatDateForAPI(selected) : "";

  const changeMonth = (nextDate) => {
    onVisibleDateChange?.(formatDateForAPI(nextDate));
  };

  return (
    <div className={`rounded-2xl border border-[#DED7CC] bg-[#FDFBF7] p-4 shadow-sm ${className}`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => changeMonth(subMonths(current, 1))}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#DED7CC] bg-white text-[#224032] transition hover:bg-[#E9EEE7]"
          aria-label="Previous month"
        >
          <CaretLeft size={18} weight="bold" />
        </button>
        <div className="text-center">
          <p className="text-sm font-semibold text-[#224032]">{format(current, "MMMM yyyy")}</p>
        </div>
        <button
          type="button"
          onClick={() => changeMonth(addMonths(current, 1))}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#DED7CC] bg-white text-[#224032] transition hover:bg-[#E9EEE7]"
          aria-label="Next month"
        >
          <CaretRight size={18} weight="bold" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1.5 text-center text-[11px] font-bold uppercase tracking-[0.08em] text-[#64756D]">
        {WEEKDAYS.map((day) => (
          <div key={day}>{day}</div>
        ))}
      </div>

      <div className="mt-2 grid grid-cols-7 gap-1.5">
        {grid.flat().map((cell) => {
          const disabled = isDateDisabled(cell.date, { minDate, maxDate, disabledWeekdays, disabledDates }) || !cell.isCurrentMonth;
          const isSelected = cell.dateKey === selectedKey;
          return (
            <button
              key={cell.dateKey}
              type="button"
              disabled={disabled}
              onClick={() => onSelect?.(cell.dateKey)}
              className={[
                "relative flex h-10 items-center justify-center rounded-xl text-sm font-semibold transition",
                isSelected ? "bg-[#224032] text-white shadow-sm" : "text-[#224032]",
                cell.isToday && !isSelected ? "ring-2 ring-[#C57053]/70" : "",
                disabled ? "cursor-not-allowed text-[#A7B1AB] opacity-45" : "hover:bg-[#E9EEE7] active:scale-[0.98]",
                !cell.isCurrentMonth ? "bg-transparent" : "",
              ].join(" ")}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
